/**
 * Injects FAQPage JSON-LD at runtime for the Q&As a page actually renders (PBI #1193, #1270).
 *
 * Business rules:
 * - Google's FAQ structured-data policy requires every marked-up question to be visible
 *   on the same page, so callers pass the exact (already priced) items they render —
 *   the landing mini-FAQ passes its three items, /faq passes its de-duplicated categories.
 * - `mainEntityOfPage` is the page emitting the markup, not always /faq.
 * - Answers arrive priced from `mapFaqItemsWithPricing`, so dollar amounts stay aligned
 *   with AppSettings instead of being frozen at build time.
 */

import { useEffect } from 'react';
import type { FaqItem } from '../../utils/resolveFaqWithPricing';
import { buildPublicAbsoluteUrl } from '../../utils/publicSiteUrl';

const SCRIPT_ID = 'ss-faq-json-ld';

interface FaqJsonLdProps {
  /** The Q&A items rendered on this page, in display order. */
  items: ReadonlyArray<FaqItem>;
  /** Root-relative path of the page emitting the markup ('/', '/faq'). */
  pagePath: string;
}

/**
 * Builds the schema.org FAQPage payload for the given items (pure; shared with Vitest).
 */
export function buildFaqPageJsonLd(items: ReadonlyArray<FaqItem>, pageUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntityOfPage: pageUrl,
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };
}

/**
 * Mounts (and updates) an FAQPage JSON-LD script in document.head; removed on unmount so a
 * page without FAQ content never inherits stale markup during SPA navigation.
 */
export function FaqJsonLd({ items, pagePath }: FaqJsonLdProps): null {
  const serialized = JSON.stringify(buildFaqPageJsonLd(items, buildPublicAbsoluteUrl(pagePath)));

  useEffect(() => {
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    script.textContent = serialized;

    return () => {
      script?.remove();
    };
  }, [serialized]);

  return null;
}
